import { createSlice } from '@reduxjs/toolkit';
import { bulletinSettingsInitialState } from './bulletinSettingsInitialState';
import {
  uploadGeneralImage,
  fetchBulletinSettings,
  updateBulletinSettings,
  removeGeneralImage,
} from './bulletinSettings.actions';

export const bulletinSettingsSlice = createSlice({
  name: 'bulletinSettingsSlice',
  initialState: bulletinSettingsInitialState,
  reducers: {
    changeBulletinHeaderConfigs: (state, action) => {
      const { key, value } = action.payload;
      state.headerConfig[key] = value;
    },
    changeBulletinHeaderLogo: (state, action) => {
      state.headerConfig.logo = {
        ...state.headerConfig.logo,
        ...action.payload,
      };
    },
    changeBulletinHighlightWords: (state, action) => {
      state.highlightWords = action.payload;
    },
    changeBulletinGeneralConfigs: (state, action) => {
      const { parent, optionKey = null } = action.payload;
      if (optionKey) {
        Object.keys(state.generalConfigs[parent].options).map((key) => {
          state.generalConfigs[parent].options[key] = key === optionKey;
        });
      } else {
        state.generalConfigs[parent].isActive =
          !state.generalConfigs[parent].isActive;
      }
    },
    changeBulletinTitleType: (state, action) => {
      const { parent, optionKey } = action.payload;
      Object.keys(state.firstPageConfigs.titles[parent].options).map((key) => {
        state.firstPageConfigs.titles[parent].options[key] = key === optionKey;
      });
      state.firstPageConfigs.titles[parent].customName = null;
    },
    changeBulletinCustomTitle: (state, action) => {
      const { key, value } = action.payload;
      state.firstPageConfigs.titles[key].customName = value;
    },
    changeBulletinFirstPageLogo: (state, action) => {
      state.firstPageConfigs.logo = {
        ...state.firstPageConfigs.logo,
        ...action.payload,
      };
    },
    changeBulletinFirstPageCover: (state, action) => {
      const { name = null, image = null } = action.payload;
      if (image) {
        state.firstPageConfigs.cover.selectedCoverImageName = '';
        state.firstPageConfigs.cover.customCover.image = image;
      } else {
        state.firstPageConfigs.cover.selectedCoverImageName = name;
        state.firstPageConfigs.cover.customCover.image = null;
      }
    },
    setSortedPlatforms: (state, action) => {
      state.sortedPlatforms = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBulletinSettings.fulfilled, (state, action) => {
        state.headerConfig = action.payload?.headerConfig;
        state.firstPageConfigs = action.payload?.firstPageConfigs;
        state.generalConfigs = action.payload?.generalConfigs;
        state.highlightWords = action.payload?.highlightWords;
        state.sortedPlatforms = action.payload?.sortedPlatforms;

        if (action.meta.arg?.savePrevState) {
          state.prevState = {
            headerConfig: action.payload?.headerConfig,
            firstPageConfigs: action.payload?.firstPageConfigs,
            generalConfigs: action.payload?.generalConfigs,
            highlightWords: action.payload?.highlightWords,
            sortedPlatforms: action.payload?.sortedPlatforms,
          };
        }

        state.fetchRequest.loading = false;
        state.fetchRequest.error = null;
      })
      .addCase(fetchBulletinSettings.pending, (state) => {
        state.fetchRequest.loading = true;
        state.fetchRequest.error = null;
      })
      .addCase(fetchBulletinSettings.rejected, (state, action) => {
        state.fetchRequest.loading = false;
        state.fetchRequest.error = action.payload;
      })
      .addCase(updateBulletinSettings.pending, (state) => {
        state.updateRequest.loading = true;
        state.updateRequest.error = null;
      })
      .addCase(updateBulletinSettings.rejected, (state, action) => {
        state.updateRequest.loading = false;
        state.updateRequest.error = action.payload;
      })
      .addCase(updateBulletinSettings.fulfilled, (state, action) => {
        if (action.meta.arg?.savePrevState) {
          state.prevState = {
            headerConfig: action.payload?.headerConfig ?? state.headerConfig,
            firstPageConfigs:
              action.payload?.firstPageConfigs ?? state.firstPageConfigs,
            generalConfigs:
              action.payload?.generalConfigs ?? state.generalConfigs,
            highlightWords:
              action.payload?.highlightWords ?? state.highlightWords,
            sortedPlatforms:
              action.payload?.sortedPlatforms ?? state.sortedPlatforms,
          };
        }

        state.updateRequest.loading = false;
        state.updateRequest.error = null;
      })
      .addCase(uploadGeneralImage.pending, (state) => {
        state.uploadImage.loading = true;
        state.uploadImage.error = null;
      })
      .addCase(uploadGeneralImage.rejected, (state, action) => {
        state.uploadImage.loading = false;
        state.uploadImage.error = action.payload;
      })
      .addCase(uploadGeneralImage.fulfilled, (state) => {
        state.uploadImage.loading = false;
        state.uploadImage.error = null;
      })
      .addCase(removeGeneralImage.pending, (state) => {
        state.uploadImage.loading = true;
        state.uploadImage.error = null;
      })
      .addCase(removeGeneralImage.rejected, (state, action) => {
        state.uploadImage.loading = false;
        state.uploadImage.error = action.payload;
      })
      .addCase(removeGeneralImage.fulfilled, (state) => {
        state.uploadImage.loading = false;
        state.uploadImage.error = null;
      });
  },
});

export const {
  changeBulletinHeaderConfigs,
  changeBulletinGeneralConfigs,
  changeBulletinHighlightWords,
  changeBulletinTitleType,
  changeBulletinCustomTitle,
  changeBulletinFirstPageLogo,
  changeBulletinFirstPageCover,
  changeBulletinHeaderLogo,
  setSortedPlatforms,
} = bulletinSettingsSlice.actions;
